import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { Image, Modal, Tag, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import useSessionState from "../../../../hooks/useSessionState";
// import CategoryCard from "./CategoryCard";
import { DataTable, useTableFilters, RowActionsMenu, TruncatedText } from "../../../../components/data-table";
import { useCategoriesContext } from "../../../../contexts/CategoriesContext";
import { useToast } from "../../../../contexts/ToastContext";
import apiClient from "../../../../helpers/apiClient";
import deleteFile from "../../../../utilities/deleteFile";
import useCarPricingMode from "../../hooks/useCarPricingMode";
import PhotoIcon from "../../../../assets/icons/photo.svg";

const RATE_KEYS = ["hourlyRate", "dailyRate", "nightlyRate", "weeklyRate", "monthlyRate"];
const RATE_TIER_COLUMNS = [
    { key: "hourlyRate", titleKey: "hourly" },
    { key: "dailyRate", titleKey: "daily" },
    { key: "weeklyRate", titleKey: "weekly" },
    { key: "monthlyRate", titleKey: "monthly" },
];
const PRICING_ACTIVE_FILTER_KEY = "hasActivePricing";
const CATEGORY_TABLE_SCROLL_X = {
    car: 560,
    category: 1260,
};

const rateBlock = (cat, key) => (cat?.[key] && typeof cat[key] === "object" ? cat[key] : { disabled: true, rate: 0 });

const hasActivePricingRate = (cat) =>
    RATE_KEYS.some((key) => {
        const tier = rateBlock(cat, key);
        return !tier.disabled && Number(tier.rate) > 0;
    });

const isCategoryMissingDetails = (cat) => cat?.seats == null || cat?.bags == null;

const AllCategories = ({
    searchTerm = "",
    setTotalCategories,
    onEditCategory,
    onViewDetails,
    className = "",
    title,
    headerExtra,
}) => {
    const { t } = useTranslation("cars");
    const toast = useToast();
    const isCarPricingMode = useCarPricingMode();
    const { categories, categoriesLoading, fetchCategories } = useCategoriesContext();

    const [currentPage, setCurrentPage] = useSessionState("categories:page", 1);
    const [pageSize, setPageSize] = useSessionState("categories:pageSize", 10);
    const [totalItems, setTotalItems] = useState(0);
    const [actionLoadingId, setActionLoadingId] = useState(null);

    const table = useTableFilters({ persistKey: "categories", onChange: () => setCurrentPage(1) });
    const searchParams = table.filters;

    const loadCategories = useCallback(async () => {
        const params = { ...searchParams };
        if (searchTerm?.trim()) {
            params.searchTerm = searchTerm;
        }

        const { data, total } = await fetchCategories(params, { page: currentPage, limit: pageSize });
        setTotalItems(total);

        if (data.length === 0 && currentPage > 1) {
            setCurrentPage(currentPage - 1);
            return;
        }

        if (setTotalCategories) {
            setTotalCategories(total);
        }
    }, [fetchCategories, searchParams, searchTerm, currentPage, pageSize, setTotalCategories, setCurrentPage]);

    const previousSearchTerm = useRef(searchTerm);
    useEffect(() => {
        // Skip the first run so a remembered page survives coming back to the list.
        if (previousSearchTerm.current === searchTerm) return;
        previousSearchTerm.current = searchTerm;
        setCurrentPage(1);
    }, [searchTerm, setCurrentPage]);

    useEffect(() => {
        loadCategories();
    }, [loadCategories]);

    const renderRateCell = (tier) => {
        if (!tier || tier.disabled) {
            return <span className="text-gray-400">{t("disabled")}</span>;
        }
        return <span className="tabular-nums">${tier.rate}</span>;
    };

    const handleViewDetails = (categoryId) => {
        onViewDetails?.(categoryId);
    };

    const handleEditCategory = (categoryId) => {
        onEditCategory?.(categoryId);
    };

    const handleRefresh = async () => {
        await loadCategories();
    };

    const handleStatusChange = async (category) => {
        try {
            setActionLoadingId(category.categoryId);
            const newStatus = category.status === "active" ? "inactive" : "active";
            const response = await apiClient.patch(`/api/car-category-v2/${category.categoryId}`, {
                status: newStatus,
            });

            if (response?.data?.success) {
                toast.success({
                    message: t("rates.statusUpdated"),
                    description: t(`rates.categoryIsNow${newStatus === "active" ? "Active" : "Inactive"}`),
                });
                await handleRefresh();
            }
        } catch (error) {
            toast.error({
                message: t("rates.failedToUpdateStatus"),
                description: error.response?.data?.message || error.message,
            });
        } finally {
            setActionLoadingId(null);
        }
    };

    const handleDeleteCategory = (category) => {
        Modal.confirm({
            title: t("rates.areYouSure"),
            content: t("rates.cantRevert"),
            okText: t("yes"),
            cancelText: t("no"),
            centered: true,
            okButtonProps: {
                danger: true,
            },
            async onOk() {
                setActionLoadingId(category.categoryId);
                try {
                    const response = await apiClient.delete(`/api/car-category-v2/${category.categoryId}`);

                    if (response?.data?.success) {
                        toast.success({
                            message: t("rates.deleted"),
                            description: t("rates.categoryDeleted"),
                        });

                        if (category.image) {
                            await deleteFile(category.image);
                        }

                        await handleRefresh();
                    }
                } catch (error) {
                    toast.error({
                        message: t("rates.error"),
                        description:
                            error.response?.data?.message || error.message || t("rates.failedToDeleteCategory"),
                    });
                } finally {
                    setActionLoadingId(null);
                }
            },
        });
    };

    const scrollX = isCarPricingMode ? CATEGORY_TABLE_SCROLL_X.car + 160 : CATEGORY_TABLE_SCROLL_X.category;

    const columns = [
        {
            title: t("rates.categoryImage"),
            dataIndex: "image",
            width: 160,
            align: "center",
            onCell: () => ({
                style: { paddingTop: 0, paddingBottom: 0 },
            }),
            render: (image) => (
                <div className="overflow-hidden rounded-md flex items-center justify-center">
                    {image ? (
                        <Image
                            src={image}
                            alt=""
                            width={64}
                            height={48}
                            className="rounded-md object-cover"
                            style={{ objectFit: "cover" }}
                        />
                    ) : (
                        <img src={PhotoIcon} alt="" className="h-6 w-6 opacity-40" />
                    )}
                </div>
            ),
        },
        {
            title: t("rates.categoryName"),
            dataIndex: "categoryName",
            width: 220,
            ...table.getColumnSearchProps("categoryName"),
            render: (categoryName, category) => {
                const showNoRateAlert = !isCarPricingMode && !hasActivePricingRate(category);
                const showIncompleteDetailsAlert =
                    !isCarPricingMode && hasActivePricingRate(category) && isCategoryMissingDetails(category);

                return (
                    <div className="flex min-w-0 items-center gap-2">
                        <TruncatedText className="text-sm font-medium">{categoryName || "N/A"}</TruncatedText>
                        {showNoRateAlert ? (
                            <Tooltip title={t("rates.categoryNoActivePricingTooltip")}>
                                <ExclamationCircleFilled
                                    style={{ fontSize: "0.9rem", color: "#ef4444", cursor: "help", flexShrink: 0 }}
                                />
                            </Tooltip>
                        ) : null}
                        {showIncompleteDetailsAlert ? (
                            <Tooltip title={t("rates.categoryIncompleteTooltip")}>
                                <ExclamationCircleFilled
                                    style={{ fontSize: "0.9rem", color: "#faad14", cursor: "help", flexShrink: 0 }}
                                />
                            </Tooltip>
                        ) : null}
                    </div>
                );
            },
        },
        ...(!isCarPricingMode
            ? [
                  {
                      title: t("rate"),
                      key: "rate",
                      ...table.getColumnSelectProps(PRICING_ACTIVE_FILTER_KEY, [
                          { value: "true", label: t("withActivePricing") },
                          { value: "false", label: t("withoutActivePricing") },
                      ]),
                      children: RATE_TIER_COLUMNS.map(({ key, titleKey }) => ({
                          title: t(titleKey),
                          key,
                          width: 90,
                          align: "right",
                          render: (_, category) => renderRateCell(rateBlock(category, key)),
                      })),
                  },
              ]
            : []),
        {
            title: t("status"),
            dataIndex: "status",
            width: 120,
            align: "center",
            ...table.getColumnSelectProps("status", [
                { value: "active", label: t("rates.active") },
                { value: "inactive", label: t("rates.deactive") },
            ]),
            render: (status) => (
                <Tag color={status === "active" ? "green" : "red"} className="!m-0">
                    {status === "active" ? t("rates.active") : t("rates.deactive")}
                </Tag>
            ),
        },
        ...(!isCarPricingMode
            ? [
                  {
                      title: t("seats"),
                      dataIndex: "seats",
                      width: 100,
                      align: "center",
                      render: (seats) => <span className="text-sm">{seats ?? "N/A"}</span>,
                  },
                  {
                      title: t("rates.bags"),
                      dataIndex: "bags",
                      width: 100,
                      align: "center",
                      render: (bags) => <span className="text-sm">{bags ?? "N/A"}</span>,
                  },
              ]
            : []),
        {
            title: t("rates.totalCars"),
            dataIndex: "carCount",
            key: "carCount",
            width: 100,
            align: "center",
            render: (carCount) => <span className="text-sm tabular-nums">{carCount ?? 0}</span>,
        },
        {
            title: <span className="sr-only">{t("action")}</span>,
            key: "action",
            width: 64,
            align: "center",
            fixed: "right",
            render: (_, category) => {
                const actionItems = [
                    { label: t("rates.viewDetails"), key: "viewDetails" },
                    { label: t("edit"), key: "edit" },
                    {
                        label: category.status === "active" ? t("rates.setAsDeactive") : t("rates.setAsActive"),
                        key: "toggleStatus",
                    },
                    { label: t("delete"), key: "delete", danger: true },
                ];

                const onClickAction = ({ key }) => {
                    if (key === "viewDetails") {
                        handleViewDetails(category.categoryId);
                        return;
                    }
                    if (key === "edit") {
                        handleEditCategory(category.categoryId);
                        return;
                    }
                    if (key === "toggleStatus") {
                        handleStatusChange(category);
                        return;
                    }
                    if (key === "delete") {
                        handleDeleteCategory(category);
                    }
                };

                return (
                    <RowActionsMenu
                        items={actionItems}
                        onClick={onClickAction}
                        label={t("action")}
                        disabled={actionLoadingId === category.categoryId}
                    />
                );
            },
        },
    ];

    const dataSource = useMemo(() => {
        const mapped = categories.map((category) => ({
            ...category,
            key: category.categoryId || category._id,
        }));

        const pricingFilter = searchParams[PRICING_ACTIVE_FILTER_KEY];
        if (isCarPricingMode || !pricingFilter) {
            return mapped;
        }

        return mapped.filter((category) => {
            const hasRate = hasActivePricingRate(category);
            if (pricingFilter === "true") return hasRate;
            if (pricingFilter === "false") return !hasRate;
            return true;
        });
    }, [categories, isCarPricingMode, searchParams]);

    return (
        <div className={className}>
            <DataTable
                title={title}
                count={totalItems}
                headerExtra={headerExtra}
                filters={table}
                search={{ key: "categoryName", placeholder: t("rates.categoryName") }}
                filterLabels={{
                    categoryName: t("rates.categoryName"),
                    status: t("status"),
                    [PRICING_ACTIVE_FILTER_KEY]: t("rate"),
                }}
                columns={columns}
                dataSource={dataSource}
                loading={categoriesLoading || Boolean(actionLoadingId)}
                rowKey="categoryId"
                scroll={{ x: scrollX }}
                pagination={{
                    total: totalItems,
                    current: currentPage,
                    pageSize,
                    onChange: setCurrentPage,
                    onPageSizeChange: setPageSize,
                    pageSizeOptions: [10, 20, 30, 50],
                }}
            />
        </div>
    );
};

AllCategories.propTypes = {
    searchTerm: PropTypes.string,
    setTotalCategories: PropTypes.func,
    onEditCategory: PropTypes.func,
    onViewDetails: PropTypes.func,
    className: PropTypes.string,
    title: PropTypes.node,
    headerExtra: PropTypes.node,
};

export default AllCategories;
